import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { generateSlug } from "@/lib/utils";
import { useTourCategories } from "@/hooks/useTourCategories";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Eye, Calendar, Image } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  short_description: string | null;
  long_description: string | null;
  main_image_url: string | null;
  gallery_images_urls: string[] | null;
  category_id: string | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  tour_categories?: {
    name: string;
    icon: string;
  };
}

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    short_description: "",
    long_description: "",
    main_image_url: "",
    gallery_images_urls: [] as string[],
    category_id: "",
    is_active: true,
    is_featured: false,
  });

  const { categories } = useTourCategories();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchBlogs();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }

    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (!adminUser) {
      navigate('/admin/login');
    }
  };

  const fetchBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          tour_categories (
            name,
            icon
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching blog posts:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los posts del blog",
          variant: "destructive",
        });
        return;
      }

      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los posts del blog",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      short_description: "",
      long_description: "",
      main_image_url: "",
      gallery_images_urls: [],
      category_id: "no-category",
      is_active: true,
      is_featured: false,
    });
    setEditingBlog(null);
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingBlog ? prev.slug : generateSlug(title)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const blogData = {
        ...formData,
        category_id: formData.category_id === "no-category" ? null : formData.category_id,
      };

      let error;
      if (editingBlog) {
        const { error: updateError } = await supabase
          .from('blog_posts')
          .update(blogData)
          .eq('id', editingBlog.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('blog_posts')
          .insert([blogData]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving blog post:', error);
        toast({
          title: "Error",
          description: `No se pudo ${editingBlog ? 'actualizar' : 'crear'} el post`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: `Post ${editingBlog ? 'actualizado' : 'creado'} exitosamente`,
      });

      setIsDialogOpen(false);
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast({
        title: "Error",
        description: `No se pudo ${editingBlog ? 'actualizar' : 'crear'} el post`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (blog: BlogPost) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      short_description: blog.short_description || "",
      long_description: blog.long_description || "",
      main_image_url: blog.main_image_url || "",
      gallery_images_urls: blog.gallery_images_urls || [],
      category_id: blog.category_id || "no-category",
      is_active: blog.is_active,
      is_featured: blog.is_featured,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de que quieres eliminar este post?")) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting blog post:', error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el post",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: "Post eliminado exitosamente",
      });

      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el post",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Gestión del Blog</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingBlog ? "Editar Post" : "Nuevo Post"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="Título del post"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug *</Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="url-del-post"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="short_description">Descripción Corta</Label>
                  <Textarea
                    id="short_description"
                    value={formData.short_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                    placeholder="Descripción breve del post"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="long_description">Descripción Larga</Label>
                  <Textarea
                    id="long_description"
                    value={formData.long_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, long_description: e.target.value }))}
                    placeholder="Contenido completo del post (puedes usar HTML)"
                    rows={10}
                  />
                  <p className="text-sm text-muted-foreground">
                    Puedes usar HTML básico como &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt;, etc.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="main_image_url">URL Imagen Principal</Label>
                    <Input
                      id="main_image_url"
                      value={formData.main_image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, main_image_url: e.target.value }))}
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Categoría</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-category">Sin categoría</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Galería de Imágenes (URLs separadas por comas)</Label>
                  <Textarea
                    value={formData.gallery_images_urls.join(', ')}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      gallery_images_urls: e.target.value.split(',').map(url => url.trim()).filter(Boolean)
                    }))}
                    placeholder="https://imagen1.jpg, https://imagen2.jpg"
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label htmlFor="is_active">Activo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                    />
                    <Label htmlFor="is_featured">Destacado</Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Guardando..." : editingBlog ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {blogs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Image className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hay posts en el blog</h3>
              <p className="text-muted-foreground text-center mb-6">
                Crea tu primer post para comenzar a compartir contenido.
              </p>
              <Button onClick={() => {resetForm(); setIsDialogOpen(true)}}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primer Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <Card key={blog.id} className="group hover:shadow-lg transition-all duration-300">
                {blog.main_image_url && (
                  <div className="aspect-video overflow-hidden rounded-t-lg">
                    <img
                      src={blog.main_image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-2">
                    {blog.is_featured && (
                      <Badge variant="default">Destacado</Badge>
                    )}
                    <Badge variant={blog.is_active ? "outline" : "secondary"}>
                      {blog.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                    {blog.tour_categories && (
                      <Badge variant="outline">{blog.tour_categories.name}</Badge>
                    )}
                  </div>
                  <CardTitle className="line-clamp-2">{blog.title}</CardTitle>
                  {blog.short_description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {blog.short_description}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {new Date(blog.created_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(blog)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(blog.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;